// src/components/DriverDashboard.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { getTodayCheckForDriver } from '../services/vehicleCheckService';

const DriverDashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [driverData, setDriverData] = useState(null);
  const [assignedVehicles, setAssignedVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todayCheck, setTodayCheck] = useState(null);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  useEffect(() => {
    const fetchDriverData = async () => {
      if (!currentUser) return;

      try {
        // Fetch driver information
        const driversRef = collection(db, 'drivers');
        const driverQuery = query(driversRef, where('uid', '==', currentUser.uid));
        const driverSnapshot = await getDocs(driverQuery);

        if (!driverSnapshot.empty) {
          const driver = {
            id: driverSnapshot.docs[0].id,
            ...driverSnapshot.docs[0].data()
          };
          setDriverData(driver);

          // Fetch active vehicle assignments
          const assignmentsRef = collection(db, 'vehicle_assignments');
          const assignmentQuery = query(
            assignmentsRef,
            where('driver_id', '==', driver.id),
            where('status', '==', 'active')
          );
          const assignmentSnapshot = await getDocs(assignmentQuery);

          // Fetch vehicle details for each assignment
          const vehicles = [];
          for (const assignmentDoc of assignmentSnapshot.docs) {
            const assignment = assignmentDoc.data();
            const vehicleRef = doc(db, 'vehicles', assignment.vehicle_id);
            const vehicleDoc = await getDoc(vehicleRef);

            if (vehicleDoc.exists()) {
              vehicles.push({
                id: vehicleDoc.id,
                ...vehicleDoc.data(),
                assignmentId: assignmentDoc.id,
                startDate: assignment.start_date,
                endDate: assignment.end_date
              });
            }
          }
          setAssignedVehicles(vehicles);

          // Fetch today's vehicle check status
          const todayCheckData = await getTodayCheckForDriver(driver.id);
          setTodayCheck(todayCheckData);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching driver data:', error);
        setLoading(false);
      }
    };

    fetchDriverData();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Logout */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Be Fleet</h1>
              <p className="text-xs text-gray-500">Driver Portal</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span>Logout</span>
          </button>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome, {driverData?.name || 'Driver'}!
          </h1>
          <p className="text-gray-600 mt-2">
            View your assigned vehicles and vehicle status
          </p>
        </div>

      {/* Assigned Vehicle with Daily Check Status */}
      {assignedVehicles.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <svg
            className="w-16 h-16 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-gray-600 text-lg font-medium">No vehicle assigned to you yet.</p>
          <p className="text-gray-500 text-sm mt-2">
            Contact your administrator for vehicle assignment.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {assignedVehicles.map((vehicle) => (
            <div key={vehicle.id}>
              {/* Vehicle Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="mb-4 sm:mb-0">
                    <h2 className="text-2xl font-bold mb-1">
                      {vehicle.make} {vehicle.model}
                    </h2>
                    <p className="text-blue-100 text-sm">{vehicle.year} • {vehicle.registration_no}</p>
                  </div>
                  <span
                    className={`px-3 py-1 text-sm font-medium rounded-full self-start sm:self-auto ${
                      vehicle.status === 'Active'
                        ? 'bg-green-100 text-green-800'
                        : vehicle.status === 'Maintenance'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {vehicle.status}
                  </span>
                </div>
              </div>

           

              {/* Today's Daily Check Status */}
              <div className={`p-6 ${
                todayCheck
                  ? todayCheck.status === 'approved'
                    ? 'bg-green-50'
                    : todayCheck.status === 'rejected'
                    ? 'bg-red-50'
                    : 'bg-yellow-50'
                  : 'bg-blue-50'
              }`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                      {todayCheck ? (
                        todayCheck.status === 'approved' ? (
                          <>
                            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Today's Check - Approved
                          </>
                        ) : todayCheck.status === 'rejected' ? (
                          <>
                            <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Today's Check - Rejected
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Today's Check - Pending Review
                          </>
                        )
                      ) : (
                        <>
                          <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          Daily Check Not Submitted
                        </>
                      )}
                    </h3>
                    <p className="text-sm text-gray-700">
                      {todayCheck
                        ? new Date(todayCheck.checkDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'Please complete your daily vehicle inspection'}
                    </p>
                    {todayCheck && todayCheck.reviewNote && (
                      <div className="mt-3 p-3 bg-white border border-gray-300 rounded-lg">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Admin Review Note:</p>
                        <p className="text-sm text-gray-900">{todayCheck.reviewNote}</p>
                      </div>
                    )}
                  </div>
                  {(!todayCheck || todayCheck.status === 'rejected') && (
                    <button
                      onClick={() => navigate('/daily-check')}
                      className={`w-full sm:w-auto text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center ${
                        todayCheck?.status === 'rejected'
                          ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                      style={{marginTop:60}}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      {todayCheck?.status === 'rejected' ? 'Edit and Resubmit Report' : 'Start Daily Check'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Vehicle Check</h3>
              <p className="text-sm text-gray-600">
                Perform regular vehicle inspections before driving
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Need Help?</h3>
              <p className="text-sm text-gray-600">
                Contact your fleet manager for assistance
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default DriverDashboard;
